import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, Award, Users, Shield, Target, Heart } from "lucide-react";

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: "Integrity",
      description: "We uphold the highest ethical standards in all our dealings, ensuring your financial information is handled with utmost honesty and transparency.",
    },
    {
      icon: Target,
      title: "Accuracy",
      description: "Every return is meticulously reviewed to ensure precision. We leave no stone unturned in finding every legitimate deduction.",
    },
    {
      icon: Heart,
      title: "Client Focus",
      description: "Your success is our success. We take time to understand your unique situation and provide personalized solutions.",
    },
    {
      icon: Users,
      title: "Expertise",
      description: "Our team of licensed professionals stays current with tax law changes to provide you the best possible service.",
    },
  ];

  const milestones = [
    { year: "2009", event: "Goldspot Tax Service LLC founded" },
    { year: "2012", event: "Expanded to serve 500+ clients annually" },
    { year: "2015", event: "Opened current Financial District office" },
    { year: "2018", event: "Introduced electronic filing services" },
    { year: "2021", event: "Reached 5,000 satisfied clients milestone" },
    { year: "2024", event: "Launched online appointment booking" },
  ];

  return (
    <div className="pt-20 min-h-screen" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm uppercase tracking-widest text-[#D4AF37]">About Us</span>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-none mt-4 font-serif">
                Your Trusted Tax Partner Since 2009
              </h1>
              <p className="text-lg leading-relaxed text-muted-foreground mt-6">
                For over 15 years, Goldspot Tax Service LLC has been helping individuals 
                navigate the complexities of tax preparation with expertise, integrity, 
                and personalized care.
              </p>
              <div className="flex gap-4 mt-8">
                <a href="tel:+12027042459">
                  <Button 
                    className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-semibold px-8 py-6 rounded-sm"
                    data-testid="about-call-btn"
                  >
                    Call Us Today
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1758520144667-3041caeff3c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBhY2NvdW50YW50JTIwd29ya2luZyUyMG1vZGVybiUyMG9mZmljZSUyMGRhcmslMjB0aGVtZXxlbnwwfHx8fDE3NzI3NjU0NjR8MA&ixlib=rb-4.1.0&q=85"
                alt="Professional accountant at work"
                className="w-full h-auto object-cover border border-white/5"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-sm uppercase tracking-widest text-[#D4AF37]">Our Mission</span>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mt-4 font-serif">
              Simplifying Tax Season, Maximizing Your Returns
            </h2>
            <div className="section-divider mx-auto mt-6 mb-8" />
            <p className="text-lg text-muted-foreground leading-relaxed">
              Our mission is to provide exceptional tax preparation services that simplify 
              the tax process for our clients while ensuring they receive every deduction 
              and credit they're entitled to. We believe everyone deserves access to 
              professional, reliable tax services delivered with integrity and care.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-widest text-[#D4AF37]">Our Values</span>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mt-4 font-serif">
              What We Stand For
            </h2>
            <div className="section-divider mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={value.title}
                className="text-center opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`value-${value.title.toLowerCase()}`}
              >
                <div className="w-16 h-16 bg-[#0A0A0A] border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-semibold mb-3 font-serif">{value.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0A0A0A] border-y border-white/5" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: "15+", label: "Years in Business" },
              { number: "5,000+", label: "Satisfied Clients" },
              { number: "99%", label: "Accuracy Rate" },
              { number: "48hr", label: "Average Turnaround" },
            ].map((stat, index) => (
              <div 
                key={stat.label}
                className="stat-card opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 md:py-32" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-widest text-[#D4AF37]">Our Journey</span>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mt-4 font-serif">
              Milestones
            </h2>
            <div className="section-divider mx-auto mt-6" />
          </div>

          <div className="max-w-3xl mx-auto">
            {milestones.map((milestone, index) => (
              <div 
                key={milestone.year}
                className="flex gap-6 mb-8 opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex-shrink-0 w-20">
                  <span className="text-[#D4AF37] font-bold font-serif text-xl">{milestone.year}</span>
                </div>
                <div className="flex-grow pb-8 border-l border-white/10 pl-6 relative">
                  <div className="absolute left-0 top-2 w-2 h-2 bg-[#D4AF37] -translate-x-1/2" />
                  <p className="text-muted-foreground">{milestone.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="why-choose-about">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm uppercase tracking-widest text-[#D4AF37]">Why Goldspot</span>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mt-4 font-serif">
                What Sets Us Apart
              </h2>
              <div className="section-divider mt-6 mb-8" />
              
              <div className="space-y-6">
                {[
                  "Licensed CPAs and Tax Professionals",
                  "IRS Authorized E-File Provider",
                  "Maximum Refund Guarantee",
                  "Year-Round Support & Assistance",
                  "Transparent, Competitive Pricing",
                  "Secure Data Protection",
                ].map((item) => (
                  <div key={item} className="flex gap-3 items-center">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1696861273647-92dfe8bb697c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHBlb3BsZSUyMHNoYWtpbmclMjBoYW5kcyUyMGx1eHVyeSUyMG9mZmljZXxlbnwwfHx8fDE3NzI3NjU0NjV8MA&ixlib=rb-4.1.0&q=85"
                alt="Business partnership handshake"
                className="w-full h-auto object-cover border border-white/5"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight font-serif">
            Ready to Experience the Goldspot Difference?
          </h2>
          <p className="text-lg text-muted-foreground mt-6 max-w-2xl mx-auto">
            Join thousands of satisfied clients who trust us with their tax preparation needs. Walk-ins welcome!
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10">
            <a href="tel:+12027042459">
              <Button 
                className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-semibold px-8 py-6 text-lg rounded-sm"
                data-testid="about-cta-call-btn"
              >
                Call Now
              </Button>
            </a>
            <Link to="/contact">
              <Button 
                variant="outline"
                className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] px-8 py-6 text-lg rounded-sm"
                data-testid="about-cta-contact-btn"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
